import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  TextField,
  Grid,
  Autocomplete,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Tooltip,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  DialogActions,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';


export default function AddressForm(props) {
  const [formData, setFormData] = useState({
    dashboardName: '',
    ip: '',
    port: '',
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [planData, setPlanData] = useState([]);
  const [subscriptionPlanId, setSelectedPlanId] = useState(null);
  const [purchaseSubscribeId, setPurchaseSubscribeId] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIpType, setSelectedIpType] = useState('');
  const [selectedOSType, setSelectedOSType] = useState('');
  const token = localStorage.getItem('token');
  const [privateIpGuideOpen, setPrivateIpGuideOpen] = useState(false);

  const fetchPlanData = async (token, setPlanData, setLoading) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/getUserSubscriptionPlansWithIp`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.data?.success) {
        const planDataArray = Object.keys(response.data.data).map((key) => ({
          purchaseSubscribeId: key,
          subscriptionPlanId: response.data.data[key].subscriptionPlanId,
          planName: response.data.data[key].planName,
          ipCount: response.data.data[key].ipCount,
        }));
        setPlanData(planDataArray);
        setLoading(false);
      } else {
        toast.error(response.data.message, { position: toast.POSITION.TOP_RIGHT });
        setLoading(false);
      }
    } catch (error) {
      toast.error('Server Not Found', { position: toast.POSITION.TOP_RIGHT });
      setError('Error fetching plan data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanData(token, setPlanData, setError);
  }, [token]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDropdownChange = (_, newValue) => {
    setSelectedOption(newValue);
    const selectedPlan = planData.find((plan) => plan.purchaseSubscribeId === newValue?.value);
    if (selectedPlan) {
      setSelectedPlanId(selectedPlan.subscriptionPlanId);
      setPurchaseSubscribeId(selectedPlan.purchaseSubscribeId);
    } else {
      setSelectedPlanId(null);
      setPurchaseSubscribeId(null);
    }
  };

  const handleIpTypeChange = (event) => {
    const value = event.target.value;

    if (value === "private") {
      setPrivateIpGuideOpen(true); // open dialog
    }
    setSelectedIpType(value);
  };

  const handlePrivateIpGuideClose = () => {
    setPrivateIpGuideOpen(false);
  };
  const handleOSTypeChange = (event) => {
    setSelectedOSType(event.target.value);
  }

  const handleSubmit = async (e) => {
    if (!e.target.reportValidity()) {
      return; // Let browser show validation errors
    }

    e.preventDefault(); // After browser validates
    const userId = localStorage.getItem('userId');

    try {
      setIsLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (!formData.dashboardName || !formData.ip  || !selectedOption || !selectedIpType || !selectedOSType) {
        setError('All fields are required.');
        setIsLoading(false);
        return;
      }

      const apiUrl = `${API_BASE_URL}/addAddress`;

      const response = await axios.post(
        apiUrl,
        {
          ...formData,
          port: selectedIpType === "private" ? 9100 : formData.port,
          userId,
          subscriptionPlanId,
          purchaseSubscribeId,
          typeOfIp: selectedIpType,
          typeOfOS: selectedOSType,
        },
        config
      );


      if (response.status === 200) {
        setFormData({ dashboardName: '', ip: '', port: '' });
        setSelectedOption(null);
        setSelectedIpType('');
        setSelectedOSType('');
        setError('');
        fetchPlanData(token, setPlanData, setError);
        props.reloadData();
        toast.success('Address added successfully', { position: toast.POSITION.TOP_RIGHT });
      } else {
        toast.error(response.data.message, { position: toast.POSITION.TOP_RIGHT });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Submission failed', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  AddressForm.propTypes = {
    reloadData: PropTypes.func.isRequired,
  };

  const filteredOptions = planData.filter((plan) => plan.ipCount > 0);
  const formattedOptions = filteredOptions.map((plan, index) => ({
    label: `${index + 1}. ${plan.planName} (Remaining IP - ${plan.ipCount})`,
    value: plan.purchaseSubscribeId,
  }));

  return (
    <Card>
      <CardContent style={{ textAlign: 'center' }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={formattedOptions}
                getOptionLabel={(option) => option.label}
                value={selectedOption}
                onChange={handleDropdownChange}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} label="Subscription Plan" required />}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                required
                label="OS Type"
                value={selectedOSType}
                onChange={handleOSTypeChange}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: 2,
                  },
                }}
              >
                <MenuItem value="linuxOrMac">Linux/macOS</MenuItem>
                <MenuItem value="windows">Windows</MenuItem>
              </TextField>
            </Grid>


            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                required
                label="IP Type"
                value={selectedIpType}
                onChange={handleIpTypeChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      <Tooltip title="Click for more information about IP types (Private or Public).">
                        <RouterLink to="/dashboard/documentation?tab=2&section=itemA" style={{ textDecoration: 'none' }}>
                          <IconButton
                            sx={{
                              color: 'action.active',
                              '&:hover': {
                                color: 'blue',
                              },
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </RouterLink>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiSelect-select': { textAlign: 'left' },
                  '& .MuiInputBase-root': { paddingRight: 2 },
                }}
              >
                <MenuItem value="public">Public IP</MenuItem>
                {selectedOSType === 'linuxOrMac' && <MenuItem value="private">Private IP</MenuItem>}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                label="Dashboard Name"
                name="dashboardName"
                value={formData.dashboardName}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Used for identifying different nodes in the Watch List.">
                        <IconButton>
                          <InfoIcon color="action" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                label={selectedIpType === 'private' ? 'Hostname' : 'IP Address'}
                name="ip"
                value={formData.ip}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          selectedIpType === 'private'
                            ? 'This field is case-sensitive. Please confirm the hostname before entering it.'
                            : 'This field is case-sensitive and must be formatted correctly. Please enter a valid IP address.'
                        }
                      >
                        <IconButton>
                          <InfoIcon color="action" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                label="Port Number"
                name="port"
                type="number"
                value={formData.port}
                disabled={selectedIpType === 'private'}
                onChange={handleInputChange}
                inputProps={{
                  inputMode: 'numeric',
                  title: 'Enter a valid port number',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Specify the port number to connect to the service.">
                        <IconButton>
                          <InfoIcon color="action" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  style={{ marginTop: '10px' }}
                >
                  {isLoading ? <CircularProgress size={25} sx={{ color: 'white' }} /> : 'Submit'}
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </CardContent>
      <Dialog
        open={privateIpGuideOpen}
        onClose={handlePrivateIpGuideClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Private IP — Setup Steps</DialogTitle>

        <DialogContent dividers>
          <Typography paragraph>
            To monitor a <strong>private IP</strong> you need to complete the following two steps:
          </Typography>

          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography component="div">
                <strong>Step&nbsp;1 — Install Node Exporter</strong>
                <br />
                Node Exporter runs on the target host and exposes metrics that Prometheus can scrape.
                <Button
                  variant="text"
                  component={RouterLink}
                  to="/dashboard/documentation?tab=2&section=itemB"
                  sx={{ textTransform: "none", ml: 1 }}
                >
                  Click here
                </Button>
              </Typography>
            </li>

            <li>
              <Typography component="div" sx={{ mt: 2 }}>
                <strong>Step&nbsp;2 — Set up PushProx</strong>
                <br />
                PushProx lets a client inside the private network push metrics to the Prometheus server.
                <Button
                  variant="text"
                  component={RouterLink}
                  to="/dashboard/documentation?tab=2&section=itemD"
                  sx={{ textTransform: "none", ml: 1 }}
                >
                  Click here
                </Button>
              </Typography>
            </li>
          </Box>

          <Typography paragraph color="text.secondary">
            <strong>Important:</strong> Complete <strong>Step&nbsp;1</strong> first
            (install Node Exporter on the machine you want to monitor). Once Step&nbsp;1
            is finished, proceed to <strong>Step&nbsp;2</strong> to connect that host
            with PushProx. Doing them in order ensures the exporter is present and ready
            when you configure PushProx.
          </Typography>

          <Typography paragraph color="text.secondary">
            <strong>Note:</strong> Copy <strong>HostName</strong> displayed in the <strong>Step&nbsp;2</strong>.
            And paste that in Hostname field.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handlePrivateIpGuideClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  );
}
